import { Component, OnInit, OnDestroy, TemplateRef, ChangeDetectionStrategy} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription, ReplaySubject, Observable, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppService } from '@irb/app.service';
import { UtilityService } from '@irb-common/services/utility.service';
import { IEvent, IrbSettingsEvents, IrbAppEvents } from '@irb-common/events';
import { IrbDashboardService } from '@irb/irb-dashboard/irb-dashboard.service';
import { ISystemMessage } from '@irb/irb-dashboard/irb-dashboard.model';
import { SettingsPage } from '@irb/irb-settings/irb-settings.page';
import { SettingsAnnouncementsGridLayoutService} from '@irb/irb-settings/settings-announcements/settings-announcements.grid-layout.service';
import { IrbSettingsService } from '@irb/irb-settings/irb-settings.service';
import { IAlertDialogData } from "@irb/app.model";
import { IGlobalSettingsSaveResponse } from '@irb/irb-settings/irb-settings.model';

@Component({ standalone: false,
  selector: 'irb-settings-announcements',
  templateUrl: './settings-announcements.component.html',
  styleUrls: ['./settings-announcements.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class SettingsAnnouncementsComponent implements SettingsPage<Array<ISystemMessage>>, OnInit, OnDestroy {
  /* grid */
  grid: any;
  gridOptions: any;
  footerContent: string;
  frameworkComponents: any;

  /* page */
  _globalSettings: Array<ISystemMessage>;
  originalGlobalSettings: Array<ISystemMessage>;
  _isModified: boolean;
  showUnsavedChangesWarning: boolean = false;
  validationErrors: string[];
  validationErrorTemplates: TemplateRef<any>[];

  /* events */
  eventSubscription: Subscription;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  /* getter setter */
  get isModified() {
    return this._isModified;
  }

  set isModified(newIsModified: boolean) {
    this._isModified = newIsModified;
  }

  get globalSettings() {
    return this._globalSettings;    
  }

  set globalSettings(newGlobalSettings: Array<ISystemMessage>) {
    this._globalSettings = newGlobalSettings;
  }

  /* */
  constructor(
    private gls: SettingsAnnouncementsGridLayoutService, 
    private as: AppService, 
    private util: UtilityService,
    private dbs: IrbDashboardService,
    private iss: IrbSettingsService) {
      this.eventSubscription = this.as.getEvents().subscribe((s) => { this.processEvent(s); });
  }

  ngOnInit(): void {
    this.gridOptions = {
      columnDefs: this.gls.getColumnDefs(this),      
      floatingFilter: false,
      overlayNoRowsTemplate: "<span class='ag-overlay-loading-center ag-overlay-message'>No Custom values defined.</span>",
      context: {componentParent: this}
    };
    
    this.frameworkComponents = this.gls.getFrameworkComponents();
  }

  ngOnDestroy() {
    this.eventSubscription.unsubscribe();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onGridReady(params: any) {
    this.grid = params;    
    this.get();
  }

  processEvent(event: IEvent) {
    switch(event.type) {
      case IrbSettingsEvents.SAVE: 
        this.save();
        break;
      case IrbSettingsEvents.UNSAVED_CHANGES:
        if(this.isModified) {
          this.showUnsavedChangesWarning = true;
        }
        break;
      case IrbSettingsEvents.REVERT_CHANGES:
        this.revert();
        break;
    }
  }

  load(data: Array<ISystemMessage>) {
    this.globalSettings = data;
    this.originalGlobalSettings = this.util.clone(this.globalSettings);
    this.isModified = false;
    this.showUnsavedChangesWarning = false;
    this.as.fireEvent(IrbSettingsEvents.IS_NOT_MODIFIED);
    this.updateGrid();
  }

  get() {
    this.dbs.getSystemMessageInventory().pipe(takeUntil(this.destroyed$)).subscribe((data: Array<ISystemMessage>) => {
      this.load(data);
    });
  }

  onInputChange() {
    window.setTimeout(() => {
      this.isModified = !this.util.compare(this.originalGlobalSettings,this.globalSettings);
      this.as.fireEvent((this.isModified) ? IrbSettingsEvents.IS_MODIFIED : IrbSettingsEvents.IS_NOT_MODIFIED);
      this.showUnsavedChangesWarning = false;
      this.validateInput().subscribe(_validationErrors => {});
    });
  }

  validateInput(): Observable<string[]> {
    this.validationErrors = [];
    this.validationErrorTemplates = [];
    return of(this.validationErrors);
  }

  updateGrid() {
    if (this.grid && this.grid.api) {
      this.grid.api.setGridOption('rowData', this.globalSettings);
      this.updateGridFooter();
    }
  }

  updateGridFooter() {
    if(this.globalSettings && this.grid && this.grid.api) {
      this.footerContent = this.grid.api.getDisplayedRowCount() + " of " + this.globalSettings.length + " values";
    }
  }

  save() {
    this.as.fireEvent(IrbSettingsEvents.SAVE_START);
    let d: any = this.globalSettings.filter((x: ISystemMessage) => !x.messageId || x.isModified);
    d = d.map((x: ISystemMessage) => this.util.getObjectValues(x, ["messageId","messageTitle","messageText","expirationDate","isActive"]));
    if (d.length !== 0) {
      d.forEach((m: any) => {
        m.messageText = m.messageText.replace(/[\u00A0-\u9999]/g, (i: any) => {
          return '&#'+i.charCodeAt(0)+';';
        });
      });
    }

    this.iss.saveAnnouncementSettings(d).subscribe((data: any) => {
      const response = data as IGlobalSettingsSaveResponse;
    
      if(response.responseStatus === "SUCCESS") {
        this.get();
        this.as.fireEvent(IrbSettingsEvents.SAVE_SUCCESS, "Announcement settings were updated.");
      } else if(response.responseStatus === "BUSINESS_ERR_MSG" || response.responseStatus === "EXCEPTION") {
        this.as.fireEvent(IrbSettingsEvents.SAVE_ERROR);
      } else if(response.responseStatus === "STALEDATAEXCEPTION") {
        this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
          dTitle: 'Refresh Required',
          dProceedBtnLabel: 'Refresh',
          msg: "You are attempting to edit announcements that were just modified by someone else. To see the updated announcements, you need to refresh the page.",
          onProceed: () => {this.get();}
        } as IAlertDialogData);
      }    
    },() => {
      this.as.fireEvent(IrbSettingsEvents.SAVE_ERROR);
    });
  }

  revert() {
    if(this.isModified) {
      this.load(this.originalGlobalSettings);
    }
  }

  create() {
    this.as.fireEvent(IrbSettingsEvents.SHOW_ANNOUNCEMENT_EDIT, {
      onProceed: this.createMessage.bind(this)
    });
  }
  createMessage(data:ISystemMessage) {
    this.globalSettings.push(data);
    this.updateGrid();
    this.onInputChange();
  }

  update(data: ISystemMessage) {
    this.as.fireEvent(IrbSettingsEvents.SHOW_ANNOUNCEMENT_EDIT, {
      systemMessage: data,
      onProceed: this.updateMessage.bind(this)
    });
  }
  updateMessage(data:ISystemMessage) {
    const i = this.globalSettings.findIndex((item: ISystemMessage) => {
      if(item.messageId) {
        return item.messageId === data.messageId;
      } else {
        return item.tempId === data.tempId;
      }
    });

    if(i !== -1) {
      this.globalSettings[i] = this.util.clone(data);
      this.updateGrid();
      this.onInputChange();
    }
  }
}
